import React, { useEffect, useState } from "react";
import useCeo from "../../hooks/useCEO";
import { useReactTable, getCoreRowModel, flexRender, createColumnHelper } from "@tanstack/react-table";
import { useUnidades } from "../../hooks/test/useUnidades";
import { Spinner } from "../../components/Spinner";
import Paginacion from "../../components/customComponents/Paginacion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import ModalComponent from "../../components/customComponents/Modals/ModalComponent";
import Swal from "sweetalert2";

export const GestionDeReportes = () => {

    useCeo({
        title: "Gestion de reportes",
        description: "Gestion de reportes finalizados",
    });

    useEffect(() => {
        loadInfo();
    }, []);

    const {
        getRegistrosSeguridadPage,
        patchEventoSeguridadId,
        getSeguimientoRegistrosSeguridad
    } = useUnidades();

    const [registrosSeguridad, setRegistrosSeguridad] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginado, setPaginado] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [registroSeleccionado, setRegistroSeleccionado] = useState(null);
    const [observacionesEditadas, setObservacionesEditadas] = useState({});
    const [observacionesOriginales, setObservacionesOriginales] = useState({});
    const [busqueda, setBusqueda] = useState("");

    const loadInfo = async (pagina = 1, busquedaTexto = "") => {
        try {
            setLoading(true);
            const dataRegistrosPage = await getRegistrosSeguridadPage(pagina, busquedaTexto);
            if (dataRegistrosPage.ok) {
                setRegistrosSeguridad(dataRegistrosPage.data.eventos);
                setPaginado({
                    page: dataRegistrosPage.data.paginacion.pagina || 1,
                    limit: dataRegistrosPage.data.paginacion.totalRegistros || 5,
                    previousPage: dataRegistrosPage.data.paginacion.previousPage || null,
                    nextPage: dataRegistrosPage.data.paginacion.nextPage || null,
                    total: dataRegistrosPage.data.paginacion.totalRegistros || 0,
                    totalPaginas: dataRegistrosPage.data.paginacion.totalPaginas || 1,
                });
            } else {
                console.log("Error al obtener los datos");
                Swal.fire({
                    title: dataRegistrosPage.msg,
                    icon: "info",
                    timer: 2500,
                    showConfirmButton: false,
                });
            }
        } catch (err) {
            console.error(err);

        } finally {
            setLoading(false);
        }
    }

    // Manejar el cambio de página
    const handlePageChange = async (newPage) => {
        const resp = await getRegistrosSeguridadPage(newPage, busqueda);

        if (resp.ok) {
            setRegistrosSeguridad(resp.data.eventos);
            setPaginado((prev) => ({
                ...prev,
                page: newPage,
                previousPage: newPage > 1 ? newPage - 1 : null,
                nextPage: newPage < prev.totalPaginas ? newPage + 1 : null,
            }));
        }
    };

    const columnHelper = createColumnHelper();

    const columns = [
        columnHelper.accessor('folio', {
            header: 'FOLIO',
        }),
        columnHelper.accessor('evento_seguridad', {
            id: 'evento',
            header: 'EVENTO',
        }),
        columnHelper.accessor('unidad_identificador', {
            id: 'unidad',
            header: 'UNIDAD',
        }),
        columnHelper.accessor('unidad_placas', {
            id: 'placas',
            header: 'PLACAS',
        }),
        columnHelper.accessor('unidad_marca', {
            id: 'unidad_marca',
            header: 'MARCA',
        }),
        columnHelper.accessor('unidad_submarca', {
            id: 'unidad_submarca',
            header: 'SUBMARCA',
        }),
        columnHelper.accessor('entidad_federativa', {
            id: 'estado',
            header: 'ESTADO',
        }),
        columnHelper.accessor('cliente', {
            id: 'cliente',
            header: 'CLIENTE',
        }),
        columnHelper.accessor('ultimo_estatus', {
            header: 'ÚLTIMO ESTATUS',
            cell: info => (
                <div
                    style={{
                        height: '50px',
                        width: '250px',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        display: 'flex'
                    }}
                    title={info.getValue()}
                >
                    {info.getValue()}
                </div>
            ),
        }),
        columnHelper.accessor('createdAt', {
            header: 'FECHA',
            cell: info => new Date(info.getValue()).toLocaleString(),
        }),
        columnHelper.display({
            id: 'acciones',
            header: 'ACCIONES',
            cell: info => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%'  // Asegura que ocupe toda la altura de la celda
                    }}
                >
                    <FontAwesomeIcon
                        icon={faPenToSquare}
                        title="Editar"
                        style={{
                            cursor: 'pointer',
                            color: '#007bff',
                            fontSize: '20px'
                        }}
                        onClick={() => handleOpenModal(info.row.original)}
                    />
                </div>
            )
        })

    ];

    const table = useReactTable({
        data: registrosSeguridad,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleOpenModal = async (registro) => {

        let resp = await getSeguimientoRegistrosSeguridad(registro.evento_seguridad_id);

        if (resp.ok) {
            setRegistroSeleccionado(resp.evento);

            const observacionesIniciales = {};
            const observacionesOriginalesMap = {};

            resp.evento.registro_evento_seguridads.forEach((item) => {
                const valor = item.observaciones ?? "";
                observacionesIniciales[item.registro_evento_id] = valor;
                observacionesOriginalesMap[item.registro_evento_id] = valor;
            });

            setObservacionesEditadas(observacionesIniciales);
            setObservacionesOriginales(observacionesOriginalesMap);

            setModalOpen(true);
        } else {
            Swal.fire({
                title: resp.msg,
                icon: "info",
                timer: 2500,
                showConfirmButton: false,
            });
        }

    };


    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleConfirm = async () => {
        try {
            const exitosos = [];
            const fallidos = [];

            for (const [id, nuevaObservacion] of Object.entries(observacionesEditadas)) {
                const originalObservacion = observacionesOriginales[id] ?? "";
                const observacionNueva = nuevaObservacion ?? "";

                if (observacionNueva !== originalObservacion) {
                    const act = await patchEventoSeguridadId(id, { observaciones: observacionNueva });

                    if (act.ok) {
                        exitosos.push(id);
                    } else {
                        fallidos.push({ id, msg: act?.msg || "Error desconocido" });
                    }
                }
            }

            if (exitosos.length && fallidos.length === 0) {
                Swal.fire({
                    title: "Actualización completa",
                    text: `${exitosos.length} observaciones actualizadas correctamente.`,
                    icon: "success",
                    timer: 3000,
                    showConfirmButton: false,
                });
            } else if (exitosos.length || fallidos.length) {
                Swal.fire({
                    title: "Actualización parcial",
                    icon: "warning",
                    html: `
                    <p><strong>${exitosos.length}</strong> actualizaciones exitosas.</p>
                    <p><strong>${fallidos.length}</strong> fallidas:</p>
                    <ul style="text-align: left; max-height: 200px; overflow-y: auto;">
                        ${fallidos.map(f => `<li>ID ${f.id}: ${f.msg}</li>`).join("")}
                    </ul>
                `,
                    confirmButtonText: "Entendido"
                });
            } else {
                Swal.fire({
                    title: "Sin cambios",
                    text: "No se detectaron modificaciones en las observaciones.",
                    icon: "info",
                    timer: 2500,
                    showConfirmButton: false,
                });
            }

            loadInfo();
            setModalOpen(false);
        } catch (err) {
            console.error("Error actualizando observaciones:", err);
            Swal.fire({
                title: "Error crítico",
                text: "Hubo un problema al actualizar las observaciones.",
                icon: "error"
            });
        }
    };

    return (
        <>
            {loading && <Spinner />}
            <div className="row">
                <div className="ps-5 pe-2 col-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar por folio, número de unidad, placas o vin..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                    />
                </div>
                <div className="col-2 mt-2">
                    <button className="btn btn-primary" onClick={() => loadInfo(1, busqueda)}>
                        Buscar
                    </button>
                    <button className="btn btn-secondary ms-2"
                        onClick={() => { setBusqueda(""); loadInfo(); }
                        }>
                        Limpiar
                    </button>
                </div>
            </div>

            <div style={{ padding: "10px 50px" }}>
                {
                    registrosSeguridad.length !== 0 ? (
                        <>
                            <div className="table-responsive">
                                <table className="table table-striped table-hover">
                                    <thead className="">
                                        {table.getHeaderGroups().map(headerGroup => (
                                            <tr key={headerGroup.id}>
                                                {headerGroup.headers.map(header => (
                                                    <th key={header.id}>
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                    </th>
                                                ))}
                                            </tr>
                                        ))}
                                    </thead>
                                    <tbody>
                                        {table.getRowModel().rows.map(row => (
                                            <tr key={row.id}>
                                                {row.getVisibleCells().map(cell => (
                                                    <td key={cell.id} style={{textAlign: "center"}}>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="position-relative">
                                <Paginacion paginado={paginado} onPageChange={handlePageChange} />
                            </div>
                        </>

                    ) : (
                        <>
                            <center>
                                <h2>SIN DATOS QUE MOSTRAR</h2>
                            </center>
                        </>
                    )
                }
            </div>
            <ModalComponent
                isOpen={modalOpen}
                onClose={handleCloseModal}
                onConfirm={handleConfirm}
                title="EDITAR REGISTRO"
            >
                {registroSeleccionado && (
                    <div className="row" style={{ fontSize: "14px" }}>
                        <div className="col-3">
                            <label className="col-form-label"><strong>FOLIO:</strong> {registroSeleccionado.folio}</label >
                        </div>
                        <div className="col-3">
                            <label className="col-form-label"><strong>EVENTO:</strong> {registroSeleccionado.evento?.evento}</label>
                        </div>
                        <div className="col-3">
                            <label className="col-form-label"><strong>PLACAS:</strong> {registroSeleccionado.unidad?.unidad_placas}</label>
                        </div>
                        <div className="col-3">
                            <label className="col-form-label" style={{ float: "right" }}><strong>FECHA:</strong> {new Date(registroSeleccionado.createdAt).toLocaleString()}</label>
                        </div>
                        <hr />
                        <hr />
                        <div className="table-responsive">
                            <table className="table table-striped table-hover">
                                <thead>
                                    <tr>
                                        <th className="w-75 text-center" scope="col">OBSERVACIONES</th>
                                        <th style={{ width: "13%" }} scope="col">USUARIO</th>
                                        <th style={{ width: "13%" }} scope="col">FECHA</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        registroSeleccionado.registro_evento_seguridads.map((dat, i) => {

                                            return (
                                                <>
                                                    <tr key={i}>
                                                        <th className="w-75 text-center">
                                                            {
                                                                <textarea
                                                                    className="form-control"
                                                                    value={
                                                                        observacionesEditadas[dat.registro_evento_id] !== undefined
                                                                            ? observacionesEditadas[dat.registro_evento_id]
                                                                            : (dat.observaciones ?? "")
                                                                    }
                                                                    onChange={(e) =>
                                                                        setObservacionesEditadas(prev => ({
                                                                            ...prev,
                                                                            [dat.registro_evento_id]: e.target.value
                                                                        }))
                                                                    }
                                                                />
                                                            }
                                                        </th>
                                                        <th style={{ width: "13%" }} className=" text-center">{dat.usuario.name}</th>
                                                        <th style={{ width: "13%" }} className=" text-center">{new Date(dat.updatedAt).toLocaleString()}</th>
                                                    </tr>
                                                </>
                                            )

                                        })
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </ModalComponent>
        </>
    );
}